<script setup>
import { ref, computed, nextTick, watch, onUnmounted } from 'vue'
import axios from '../../axios.js'

const props = defineProps({
  isVisible: { type: Boolean, default: false }
})

const emit = defineEmits(['close', 'save'])

const messages = ref([])
const chatInput = ref('')
const pendingImage = ref(null)
const pendingImagePreview = ref(null)
const imagePath = ref(null)
const gameMeta = ref({})
const extractedRows = ref([])
const loading = ref(false)
const saving = ref(false)
const showEditor = ref(false)
const editorJson = ref('')
const chatEndRef = ref(null)
const isDragOver = ref(false)
let dragCounter = 0

const hasExtractedData = computed(() => extractedRows.value.length > 0)

function scrollChat() {
  nextTick(() => {
    chatEndRef.value?.scrollIntoView({ behavior: 'smooth' })
  })
}

function addMessage(role, content, extra = {}) {
  const message = {
    id: Date.now() + Math.random(),
    role,
    content,
    ...extra
  }

  messages.value.push(message)
  scrollChat()
  return message.id
}

function updateMessage(id, patch) {
  const message = messages.value.find(m => m.id === id)
  if (!message) return
  Object.assign(message, patch)
  scrollChat()
}

function appendMessageContent(id, chunk) {
  const message = messages.value.find(m => m.id === id)
  if (!message || !chunk) return
  message.content += chunk
  scrollChat()
}

function apiUrl(path) {
  const baseUrl = (axios.defaults.baseURL || '').replace(/\/$/, '')
  return `${baseUrl}${path}`
}

function streamHeaders() {
  const headers = {
    Accept: 'text/event-stream',
    'X-Requested-With': 'XMLHttpRequest'
  }

  const token = localStorage.getItem('auth_token')
  if (token && !token.startsWith('demo_token')) {
    headers.Authorization = `Bearer ${token}`
  }

  return headers
}

async function streamSseRequest(path, options, onEvent) {
  const response = await fetch(apiUrl(path), {
    ...options,
    headers: {
      ...streamHeaders(),
      ...(options.headers || {})
    }
  })

  if (!response.ok) {
    const text = await response.text()
    throw new Error(text || `Request failed with status ${response.status}`)
  }

  if (!response.body) {
    throw new Error('Streaming is not supported in this browser')
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { value, done } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })

    let separatorIndex
    while ((separatorIndex = buffer.indexOf('\n\n')) !== -1) {
      const rawEvent = buffer.slice(0, separatorIndex)
      buffer = buffer.slice(separatorIndex + 2)
      handleSseBlock(rawEvent, onEvent)
    }
  }

  if (buffer.trim()) {
    handleSseBlock(buffer, onEvent)
  }
}

function handleSseBlock(rawEvent, onEvent) {
  const lines = rawEvent.split(/\r?\n/)
  let event = 'message'
  const dataLines = []

  for (const line of lines) {
    if (line.startsWith('event:')) {
      event = line.slice(6).trim()
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trimStart())
    }
  }

  if (!dataLines.length) return

  const rawData = dataLines.join('\n')
  let data
  try {
    data = JSON.parse(rawData)
  } catch {
    data = { message: rawData }
  }

  onEvent(event, data)
}

function setImageFile(file) {
  if (!file?.type?.startsWith('image/')) return false

  if (pendingImagePreview.value) {
    URL.revokeObjectURL(pendingImagePreview.value)
  }

  pendingImage.value = file
  pendingImagePreview.value = URL.createObjectURL(file)
  addMessage('user', `Uploaded boxscore image: ${file.name}`, {
    imagePreview: pendingImagePreview.value
  })
  return true
}

function onImageSelect(event) {
  const file = event.target.files?.[0]
  if (file) setImageFile(file)
  event.target.value = ''
}

function onDragEnter(event) {
  event.preventDefault()
  dragCounter++
  isDragOver.value = true
}

function onDragLeave(event) {
  event.preventDefault()
  dragCounter--
  if (dragCounter <= 0) {
    dragCounter = 0
    isDragOver.value = false
  }
}

function onDragOver(event) {
  event.preventDefault()
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = 'copy'
  }
}

function onDrop(event) {
  event.preventDefault()
  dragCounter = 0
  isDragOver.value = false
  const file = event.dataTransfer?.files?.[0]
  setImageFile(file)
}

function onPaste(event) {
  if (!props.isVisible) return

  const items = event.clipboardData?.items
  if (!items?.length) return

  for (const item of items) {
    if (!item.type.startsWith('image/')) continue

    event.preventDefault()
    const blob = item.getAsFile()
    if (!blob) continue

    const ext = blob.type.split('/')[1]?.replace('jpeg', 'jpg') || 'png'
    const file = new File([blob], `pasted-boxscore-${Date.now()}.${ext}`, { type: blob.type })
    setImageFile(file)
    return
  }
}

watch(
  () => props.isVisible,
  (visible) => {
    if (visible) {
      document.addEventListener('paste', onPaste)
    } else {
      document.removeEventListener('paste', onPaste)
    }
  },
  { immediate: true }
)

onUnmounted(() => {
  document.removeEventListener('paste', onPaste)
})

async function sendMessage() {
  const text = chatInput.value.trim()

  if (!pendingImage.value && !hasExtractedData.value && !text) return

  if (text) {
    addMessage('user', text)
  }

  chatInput.value = ''
  loading.value = true

  try {
    if (pendingImage.value && !hasExtractedData.value) {
      await parseImage(text)
      pendingImage.value = null
    } else if (hasExtractedData.value && text) {
      await refineExtraction(text)
    } else if (pendingImage.value) {
      await parseImage(text)
      pendingImage.value = null
    }
  } catch (err) {
    addMessage('assistant', `Error: ${err.response?.data?.error || err.message}`, { isError: true })
  } finally {
    loading.value = false
  }
}

async function parseImage(message) {
  const form = new FormData()
  form.append('image', pendingImage.value)
  if (message) form.append('message', message)

  const progressId = addMessage('assistant', 'Connecting to AI extraction stream...\n', {
    isLoading: true,
    isStreaming: true
  })

  let resultData = null
  let streamError = null

  await streamSseRequest('/api/from-manual-data-scraper/parse-image-stream', {
    method: 'POST',
    body: form
  }, (event, data) => {
    if (event === 'status') {
      const detail = data.model ? ` (${data.model})` : ''
      appendMessageContent(progressId, `\n• ${data.message}${detail}`)
      return
    }

    if (event === 'delta') {
      appendMessageContent(progressId, data.message || '')
      return
    }

    if (event === 'error') {
      streamError = new Error(data.error || 'Parse failed')
      updateMessage(progressId, { isError: true, isLoading: false })
      appendMessageContent(progressId, `\n\nError: ${streamError.message}`)
      return
    }

    if (event === 'result') {
      resultData = data
    }
  })

  updateMessage(progressId, { isLoading: false, isStreaming: false })

  if (streamError) throw streamError
  if (!resultData?.success) throw new Error(resultData?.error || 'Parse failed')

  applyExtraction(resultData)
  addMessage('assistant', resultData.summary || `Extracted ${resultData.rows?.length || 0} player row(s). Review below, edit if needed, then confirm save.`, {
    hasTable: true
  })
}

async function refineExtraction(message) {
  const progressId = addMessage('assistant', 'Connecting to AI revision stream...\n', {
    isLoading: true,
    isStreaming: true
  })

  let resultData = null
  let streamError = null

  await streamSseRequest('/api/from-manual-data-scraper/refine-stream', {
    method: 'POST',
    body: JSON.stringify({
      image_path: imagePath.value,
      previous: { game: gameMeta.value, rows: extractedRows.value, summary: '' },
      message
    }),
    headers: {
      'Content-Type': 'application/json'
    }
  }, (event, data) => {
    if (event === 'status') {
      const detail = data.model ? ` (${data.model})` : ''
      appendMessageContent(progressId, `\n• ${data.message}${detail}`)
      return
    }

    if (event === 'delta') {
      appendMessageContent(progressId, data.message || '')
      return
    }

    if (event === 'error') {
      streamError = new Error(data.error || 'Refine failed')
      updateMessage(progressId, { isError: true, isLoading: false })
      appendMessageContent(progressId, `\n\nError: ${streamError.message}`)
      return
    }

    if (event === 'result') {
      resultData = data
    }
  })

  updateMessage(progressId, { isLoading: false, isStreaming: false })

  if (streamError) throw streamError
  if (!resultData?.success) throw new Error(resultData?.error || 'Refine failed')

  applyExtraction(resultData)
  addMessage('assistant', resultData.summary || 'Updated extraction. Please review again.', { hasTable: true })
}

function applyExtraction(data) {
  gameMeta.value = data.game || {}
  extractedRows.value = (data.rows || []).map(normalizeRow)
  imagePath.value = data.image_path || imagePath.value
  editorJson.value = JSON.stringify({ game: gameMeta.value, rows: extractedRows.value }, null, 2)
}

function normalizeRow(row) {
  const game = gameMeta.value
  return {
    ...row,
    league_name: row.league_name ?? game.league_name ?? null,
    season: row.season ?? game.season ?? null,
    game_date: row.game_date ?? game.game_date ?? null,
    game_link: row.game_link ?? game.game_link ?? null,
    game_id_string: row.game_id_string ?? game.game_id_string ?? null,
    home_team_score: row.home_team_score ?? game.home_team_score ?? null,
    away_team_score: row.away_team_score ?? game.away_team_score ?? null,
    team_id_string: row.team_id_string ?? game.home_team_id_string ?? null,
    opponent_team_id_string: row.opponent_team_id_string ?? game.away_team_id_string ?? null,
    source: row.source ?? 3
  }
}

function openJsonEditor() {
  editorJson.value = JSON.stringify({ game: gameMeta.value, rows: extractedRows.value }, null, 2)
  showEditor.value = true
}

function applyJsonEditor() {
  try {
    const parsed = JSON.parse(editorJson.value)
    gameMeta.value = parsed.game || {}
    extractedRows.value = parsed.rows || []
    showEditor.value = false
    addMessage('assistant', 'Manual JSON edits applied. Review the table and confirm when ready.', { hasTable: true })
  } catch {
    alert('Invalid JSON')
  }
}

async function confirmSave() {
  if (!hasExtractedData.value) return
  if (!confirm(`Save ${extractedRows.value.length} player row(s) to comparable_data?`)) return

  saving.value = true
  try {
    const { data } = await axios.post('/api/from-manual-data-scraper/confirm-save', {
      game: gameMeta.value,
      rows: extractedRows.value
    })

    addMessage('assistant', data.message || 'Saved successfully.')
    emit('save', data)
    setTimeout(() => closeOverlay(), 1200)
  } catch (err) {
    addMessage('assistant', `Save failed: ${err.response?.data?.error || err.message}`, { isError: true })
  } finally {
    saving.value = false
  }
}

function closeOverlay() {
  if (pendingImagePreview.value) {
    URL.revokeObjectURL(pendingImagePreview.value)
  }
  dragCounter = 0
  isDragOver.value = false
  messages.value = []
  chatInput.value = ''
  pendingImage.value = null
  pendingImagePreview.value = null
  imagePath.value = null
  gameMeta.value = {}
  extractedRows.value = []
  showEditor.value = false
  emit('close')
}

const statColumns = [
  'player_name', 'team', 'minutes', 'points', 'rebounds', 'assists',
  'field_goals_made_attempted', 'three_points_made_attempted', 'turnovers', 'steals', 'blocks', 'plus_minus'
]
</script>

<template>
  <div v-if="isVisible" class="fixed inset-0 bg-black/50 flex items-center justify-center z-[10001] p-3">
    <div class="bg-white rounded-xl shadow-2xl w-[98vw] h-[96vh] flex flex-col overflow-hidden">
      <!-- Header -->
      <div class="bg-gray-900 text-white px-5 py-3 flex items-center justify-between flex-shrink-0">
        <div>
          <h2 class="text-lg font-semibold">Manual boxscore insert</h2>
          <p class="text-xs text-gray-400">Upload image → AI extracts comparable_data → review & confirm</p>
        </div>
        <button class="text-gray-400 hover:text-white p-2" @click="closeOverlay">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="flex flex-1 min-h-0">
        <!-- Chat panel (drag & drop or Cmd+V / Ctrl+V paste) -->
        <div
          class="flex-1 flex flex-col border-r border-gray-200 min-w-0 relative"
          @dragenter="onDragEnter"
          @dragleave="onDragLeave"
          @dragover="onDragOver"
          @drop="onDrop"
        >
          <div
            v-if="isDragOver"
            class="absolute inset-0 z-20 flex items-center justify-center bg-pink-50/90 border-2 border-dashed border-pink-500 rounded-lg m-2 pointer-events-none"
          >
            <div class="text-center text-pink-700">
              <i class="fas fa-cloud-upload-alt text-4xl mb-2"></i>
              <p class="font-medium">Drop boxscore image here</p>
            </div>
          </div>

          <div class="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            <div v-if="messages.length === 0" class="text-center text-gray-500 py-12 border-2 border-dashed border-gray-200 rounded-xl mx-2">
              <i class="fas fa-image text-4xl mb-3 text-gray-300"></i>
              <p class="font-medium">Drag & drop or paste (Cmd+V / Ctrl+V) a boxscore screenshot</p>
              <p class="text-sm mt-1">Or attach via the prompt area below · matches comparable_data schema</p>
            </div>

            <div
              v-for="msg in messages"
              :key="msg.id"
              class="flex"
              :class="msg.role === 'user' ? 'justify-end' : 'justify-start'"
            >
              <div
                class="max-w-[85%] rounded-lg px-4 py-3 text-sm"
                :class="msg.role === 'user'
                  ? 'bg-pink-600 text-white'
                  : msg.isError
                    ? 'bg-red-50 text-red-800 border border-red-200'
                    : 'bg-white text-gray-800 border border-gray-200 shadow-sm'"
              >
                <img v-if="msg.imagePreview" :src="msg.imagePreview" class="max-h-40 rounded mb-2" alt="upload" />
                <p class="whitespace-pre-wrap">{{ msg.content }}</p>
                <p v-if="msg.isLoading" class="text-xs text-gray-400 mt-2"><i class="fas fa-spinner fa-spin"></i> Working...</p>
              </div>
            </div>
            <div ref="chatEndRef"></div>
          </div>

          <!-- Prompt area -->
          <div
            class="border-t border-gray-200 p-3 bg-white flex-shrink-0"
            :class="isDragOver ? 'ring-2 ring-inset ring-pink-400' : ''"
          >
            <div v-if="pendingImagePreview" class="mb-2 flex items-center gap-2">
              <img :src="pendingImagePreview" class="h-16 rounded border" alt="pending" />
              <span class="text-xs text-gray-500">Ready to send — add instructions or click Send</span>
            </div>
            <div class="flex gap-2 items-end">
              <label class="cursor-pointer flex-shrink-0 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm">
                <i class="fas fa-paperclip mr-1"></i> Image
                <input type="file" accept="image/*" class="hidden" @change="onImageSelect" />
              </label>
              <textarea
                v-model="chatInput"
                rows="2"
                class="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-pink-500"
                placeholder="Paste (Cmd+V), drop, or attach image — add instructions, then Send..."
                @keydown.enter.exact.prevent="sendMessage"
              />
              <button
                class="px-4 py-2 bg-pink-600 text-white rounded-lg text-sm hover:bg-pink-700 disabled:opacity-50"
                :disabled="loading || (!chatInput.trim() && !pendingImage && !hasExtractedData)"
                @click="sendMessage"
              >
                <i v-if="loading" class="fas fa-spinner fa-spin"></i>
                <span v-else>Send</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Review panel -->
        <div class="w-[45%] flex flex-col bg-white min-w-0">
          <div class="px-4 py-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
            <h3 class="font-semibold text-gray-800">Review extraction</h3>
            <div class="flex gap-2">
              <button
                v-if="hasExtractedData"
                class="text-xs px-2 py-1 border rounded hover:bg-gray-50"
                @click="openJsonEditor"
              >
                Edit JSON
              </button>
            </div>
          </div>

          <div v-if="!hasExtractedData" class="flex-1 flex items-center justify-center text-gray-400 text-sm p-6 text-center">
            Parsed player rows will appear here for review before save.
          </div>

          <template v-else>
            <!-- Game meta -->
            <div class="p-3 border-b border-gray-100 text-xs space-y-1 bg-gray-50 flex-shrink-0">
              <div><span class="font-medium">League:</span> {{ gameMeta.league_name || '—' }}</div>
              <div><span class="font-medium">Season:</span> {{ gameMeta.season || '—' }}</div>
              <div><span class="font-medium">Date:</span> {{ gameMeta.game_date || '—' }}</div>
              <div><span class="font-medium">Score:</span> {{ gameMeta.home_team_score ?? '?' }} – {{ gameMeta.away_team_score ?? '?' }}</div>
              <div class="truncate"><span class="font-medium">Teams:</span> {{ gameMeta.home_team_name }} vs {{ gameMeta.away_team_name }}</div>
            </div>

            <!-- Rows table -->
            <div class="flex-1 overflow-auto">
              <table class="min-w-full text-xs">
                <thead class="bg-gray-100 sticky top-0">
                  <tr>
                    <th v-for="col in statColumns" :key="col" class="px-2 py-2 text-left font-medium text-gray-600 whitespace-nowrap">
                      {{ col }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, idx) in extractedRows" :key="idx" class="border-b border-gray-100 hover:bg-pink-50/30">
                    <td v-for="col in statColumns" :key="col" class="px-2 py-1.5 whitespace-nowrap">
                      <input
                        v-model="extractedRows[idx][col]"
                        class="w-full min-w-[4rem] border-0 bg-transparent focus:bg-white focus:ring-1 focus:ring-pink-400 rounded px-1"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="p-3 border-t border-gray-200 flex-shrink-0">
              <button
                class="w-full py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium text-sm disabled:opacity-50"
                :disabled="saving"
                @click="confirmSave"
              >
                <i v-if="saving" class="fas fa-spinner fa-spin mr-1"></i>
                Confirm & save to comparable_data ({{ extractedRows.length }} rows)
              </button>
            </div>
          </template>
        </div>
      </div>

      <!-- JSON editor modal -->
      <div v-if="showEditor" class="absolute inset-0 bg-black/40 flex items-center justify-center p-6 z-10">
        <div class="bg-white rounded-lg shadow-xl w-full max-w-3xl h-[70vh] flex flex-col">
          <div class="px-4 py-3 border-b font-semibold">Edit extraction JSON</div>
          <textarea v-model="editorJson" class="flex-1 p-4 font-mono text-xs border-0 focus:ring-0" />
          <div class="px-4 py-3 border-t flex justify-end gap-2">
            <button class="px-4 py-2 text-sm border rounded" @click="showEditor = false">Cancel</button>
            <button class="px-4 py-2 text-sm bg-pink-600 text-white rounded" @click="applyJsonEditor">Apply</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
